import { useState, useEffect, useCallback, useRef } from 'react'
import { initSearch, search, isSearchReady, reshuffleDocuments } from '../lib/search'
import { Shuffle } from 'lucide-react'
import { preloadSunoLookup, isSunoCacheReady } from '../lib/data'
import type { SearchResult } from '../lib/types'
import SearchBar from '../components/SearchBar'
import PaperCard from '../components/PaperCard'
import Header from '../components/Header'
import { usePlayer } from '../lib/player'

const RESULTS_PER_PAGE = 50
const SCROLL_STATE_KEY = 'paper2song_scroll_state'

interface ScrollState {
  query: string
  displayCount: number
  scrollTop: number
}

export default function Home() {
  const [query, setQuery] = useState('')
  const [allResults, setAllResults] = useState<SearchResult[]>([])
  const [displayCount, setDisplayCount] = useState(RESULTS_PER_PAGE)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTime, setSearchTime] = useState<number>(0)
  const { currentPaperId } = usePlayer()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const restoredRef = useRef(false)

  // Restore scroll state from sessionStorage
  useEffect(() => {
    if (restoredRef.current) return
    try {
      const saved = sessionStorage.getItem(SCROLL_STATE_KEY)
      if (saved) {
        const state: ScrollState = JSON.parse(saved)
        setQuery(state.query)
        setDisplayCount(state.displayCount)
        // Scroll will be restored after results load
        restoredRef.current = true
      }
    } catch {
      // Ignore errors
    }
  }, [])

  // Initialize search and suno lookup on mount
  useEffect(() => {
    Promise.all([
      initSearch(),
      preloadSunoLookup(),
    ])
      .then(() => {
        setLoading(false)
        // Show initial results or restored query
        const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
        if (savedState) {
          try {
            const state: ScrollState = JSON.parse(savedState)
            // For actual search queries, limit to 500 results
            // For empty queries, get all documents (randomized order)
            const searchResults = state.query.trim() 
              ? search(state.query, { limit: 500 })
              : search('')
            setAllResults(searchResults)
            // Restore scroll position after a tick
            requestAnimationFrame(() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = state.scrollTop
              }
            })
          } catch {
            const initialResults = search('') // All docs, randomized
            setAllResults(initialResults)
          }
        } else {
          const initialResults = search('') // All docs, randomized
          setAllResults(initialResults)
        }
      })
      .catch((e) => {
        setError(e.message)
        setLoading(false)
      })
  }, [])

  // Save scroll state before navigating away
  useEffect(() => {
    const saveState = () => {
      if (scrollContainerRef.current) {
        const state: ScrollState = {
          query,
          displayCount,
          scrollTop: scrollContainerRef.current.scrollTop,
        }
        sessionStorage.setItem(SCROLL_STATE_KEY, JSON.stringify(state))
      }
    }

    // Save on visibility change (before navigating to song page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveState()
      }
    }

    // Save periodically and on scroll end
    const handleScroll = () => {
      saveState()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    scrollContainerRef.current?.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      scrollContainerRef.current?.removeEventListener('scroll', handleScroll)
    }
  }, [query, displayCount])

  // Handle search
  const handleSearch = useCallback((q: string) => {
    setQuery(q)
    setDisplayCount(RESULTS_PER_PAGE) // Reset pagination on new search
    
    if (!isSearchReady()) return
    
    const start = performance.now()
    // For actual search queries, limit to 500 results
    // For empty queries, get all documents (randomized order)
    const searchResults = q.trim() 
      ? search(q, { limit: 500 })
      : search('')
    const elapsed = performance.now() - start
    setSearchTime(elapsed)
    
    setAllResults(searchResults)
    
    // Reset scroll position on new search
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [])

  // Re-search when ready
  useEffect(() => {
    if (isSearchReady() && isSunoCacheReady() && !restoredRef.current) {
      handleSearch(query)
    }
  }, [handleSearch, query])

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + RESULTS_PER_PAGE)
  }

  const handleShuffle = () => {
    reshuffleDocuments()
    const shuffledResults = search('')
    setAllResults(shuffledResults)
    setDisplayCount(RESULTS_PER_PAGE)
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }

  const displayedResults = allResults.slice(0, displayCount)
  const hasMore = displayCount < allResults.length

  return (
    <div className="h-full lg:overflow-y-auto" ref={scrollContainerRef}>
      <Header />
          
          {/* Hero / Header */}
          <header className="relative overflow-hidden">
            <div className="max-w-5xl mx-auto px-4 pt-8 pb-4">
              {/* Logo + Title */}
              <div className="flex items-center justify-center gap-4 mb-3">
                <img 
                  src="/logo.jpg" 
                  alt="Paper2Song" 
                  className="w-16 h-16 md:w-20 md:h-20 rounded-xl shadow-lg"
                />
                <h1 className="text-4xl md:text-5xl font-bold">
                  <span className="text-white">Paper</span>
                  <span className="text-sonic-gradient">2Song</span>
                </h1>
              </div>
              <p className="text-surface-400 text-center text-base mb-5">
                Every NeurIPS 2025 paper as a song
              </p>
              
              <SearchBar 
                value={query}
                onChange={handleSearch}
                disabled={loading}
                placeholder={loading ? 'Loading search...' : 'Search papers by title, author, or abstract...'}
              />
            </div>
          </header>

          {/* Main Content */}
          <main className="max-w-5xl mx-auto px-4 py-4">
            {/* Results info */}
            {!loading && !error && (
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-surface-500">
                  {allResults.length > 0 ? (
                    <>
                      Showing <span className="text-surface-300 font-medium">
                        {displayedResults.length}
                      </span> of <span className="text-surface-300 font-medium">
                        {allResults.length.toLocaleString()}
                      </span> {query.trim() ? 'results' : 'papers'}
                      {searchTime > 0 && (
                        <span className="ml-2">({searchTime.toFixed(1)}ms)</span>
                      )}
                    </>
                  ) : query ? (
                    <span>No results found for "{query}"</span>
                  ) : null}
                </div>
                {!query.trim() && allResults.length > 0 && (
                  <button
                    onClick={handleShuffle}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-surface-400 hover:text-surface-200 hover:bg-surface-800 rounded-lg transition-colors"
                    title="Shuffle papers"
                  >
                    <Shuffle size={14} />
                    <span>Shuffle</span>
                  </button>
                )}
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-6 text-center">
                <p className="text-red-300">{error}</p>
              </div>
            )}

            {/* Loading state */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent-500 border-t-transparent" />
              </div>
            )}

            {/* Results */}
            {!loading && !error && (
              <div className="space-y-4">
                {displayedResults.map((result) => (
                  <PaperCard key={result.id} result={result} query={query} />
                ))}
              </div>
            )}

            {/* Load more button */}
            {!loading && !error && hasMore && (
              <div className="mt-8 text-center">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-3 bg-surface-800 hover:bg-surface-700 border border-surface-700 hover:border-surface-600 rounded-xl text-surface-200 font-medium transition-colors"
                >
                  Load more ({allResults.length - displayCount} remaining)
                </button>
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && allResults.length === 0 && query && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-800 flex items-center justify-center">
                  <svg className="w-8 h-8 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-surface-300 mb-2">No papers found</h2>
                <p className="text-surface-500">Try different keywords</p>
              </div>
            )}

            {/* Bottom padding for playbar */}
            {currentPaperId && <div className="h-24" />}
          </main>
    </div>
  )
}
